import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  History,
  FileText,
  Users,
  DollarSign,
  Settings,
  Filter,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Clean up empty filter values before sending
      const cleanParams = {};
      if (filters.entity_type) cleanParams.entity_type = filters.entity_type;
      if (filters.action) cleanParams.action = filters.action;
      if (filters.start_date) cleanParams.start_date = filters.start_date;
      if (filters.end_date) cleanParams.end_date = filters.end_date;
      
      const [logsRes, summaryRes] = await Promise.all([
        api.get('/audit', { params: cleanParams }),
        api.get('/audit/summary')
      ]);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view audit logs');
      } else {
        toast.error('Failed to load audit logs');
      }
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    loadData();
  };

  const getActionBadge = (action) => {
    const styles = {
      create: 'bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]',
      update: 'bg-[hsl(var(--brand-100))] text-[hsl(var(--brand-700))]',
      delete: 'bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]',
      void: 'bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))]',
      login: 'bg-gray-100 text-gray-600',
      logout: 'bg-gray-100 text-gray-600',
      export: 'bg-[hsl(var(--warning-soft))] text-[hsl(var(--warning))]'
    };
    return (
      <Badge className={styles[action] || 'bg-gray-100 text-gray-600'}>
        {action?.charAt(0).toUpperCase() + action?.slice(1)}
      </Badge>
    );
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      transaction: <DollarSign size={16} />,
      user: <Users size={16} />,
      company: <Settings size={16} />,
      loan: <FileText size={16} />,
      tax: <FileText size={16} />
    };
    return icons[entityType] || <FileText size={16} />;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString();
    } catch {
      return timestamp;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[hsl(var(--brand-500))]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[hsl(var(--ink-1))]" style={{ fontFamily: 'Space Grotesk' }}>
          Audit Log
        </h1>
        <p className="text-[hsl(var(--muted-ink))]">
          Track all system activity and changes
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[hsl(var(--muted-ink))]">Total Events</p>
                <p className="text-2xl font-bold">{summary?.total_entries || 0}</p>
              </div>
              <History className="h-8 w-8 text-[hsl(var(--brand-500))]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[hsl(var(--muted-ink))]">This Week</p>
                <p className="text-2xl font-bold">{summary?.recent_count || 0}</p>
              </div>
              <Activity className="h-8 w-8 text-[hsl(var(--success))]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[hsl(var(--muted-ink))]">Entity Types</p>
                <p className="text-2xl font-bold">{Object.keys(summary?.by_entity || {}).length}</p>
              </div>
              <FileText className="h-8 w-8 text-[hsl(var(--warning))]" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[hsl(var(--muted-ink))]">Action Types</p>
                <p className="text-2xl font-bold">{Object.keys(summary?.by_action || {}).length}</p>
              </div>
              <Filter className="h-8 w-8 text-[hsl(var(--brand-700))]" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter size={18} />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select 
                value={filters.entity_type} 
                onValueChange={(v) => setFilters({...filters, entity_type: v === 'all' ? '' : v})}
              >
                <SelectTrigger data-testid="filter-entity-type">
                  <SelectValue placeholder="All entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All entities</SelectItem>
                  <SelectItem value="transaction">Transaction</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="loan">Loan</SelectItem>
                  <SelectItem value="tax">Tax</SelectItem>
                  <SelectItem value="receivable">Receivable</SelectItem>
                  <SelectItem value="payable">Payable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Action</Label>
              <Select 
                value={filters.action} 
                onValueChange={(v) => setFilters({...filters, action: v === 'all' ? '' : v})}
              >
                <SelectTrigger data-testid="filter-action">
                  <SelectValue placeholder="All actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="void">Void</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                data-testid="filter-end-date"
              />
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-[hsl(var(--brand-500))] text-white rounded-md hover:bg-[hsl(var(--brand-600))] transition-colors"
              data-testid="apply-filters-btn"
            >
              Apply Filters
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History size={18} />
            Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-12">
              <History className="mx-auto h-12 w-12 text-[hsl(var(--muted-ink))]" />
              <h3 className="mt-4 text-lg font-medium">No audit logs yet</h3>
              <p className="mt-2 text-[hsl(var(--muted-ink))]">Activity will be recorded as users interact with the system</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Description</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                      <TableCell className="text-sm text-[hsl(var(--muted-ink))]">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{log.user_name || 'System'}</span>
                      </TableCell>
                      <TableCell>
                        {getActionBadge(log.action)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getEntityIcon(log.entity_type)}
                          <span className="capitalize">{log.entity_type}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md truncate">
                        {log.description || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
